import {Pattern, match} from 'ts-pattern';

import {useDispatch} from 'react-redux';

import {isNil, isNotNil} from 'ramda';

import {
  businessCaseApi,
  useCreateOfferMutation,
  useCreateSaleVehicleMutation,
  useLazyGetBusinessCaseQuery,
  useSwitchOfferSaleVehicleMutation,
} from '@dms/api/sales';
import {useFormNavigate} from '@dms/features/vehicle-forms-kit';
import {handleApiError} from '@dms/shared';

import {ApiError} from 'shared/web';

/**
 * Attaches a newly created vehicle to an existing business case as its sale vehicle,
 * mirroring the "add / change vehicle to sell" flow used inside the business case detail.
 * A business case sells a single vehicle, so the decision is:
 * - a sale vehicle already exists -> switch (replace) it with the created vehicle,
 * - an offer exists without a sale vehicle -> add the created vehicle to that offer,
 * - no offer exists yet -> create an offer holding the created vehicle.
 *
 * Only runs for the business case sale referrer that carries the business case id (i.e. the
 * create-from-existing-business-case flow), so the new business case concept flow is left
 * untouched.
 */
export const useCreateSaleVehicleForBusinessCase = () => {
  const dispatch = useDispatch();
  const {isBusinessCaseSaleReferrer, getReferrerId} = useFormNavigate();

  const [getBusinessCase] = useLazyGetBusinessCaseQuery();
  const [createSaleVehicle] = useCreateSaleVehicleMutation();
  const [createOffer] = useCreateOfferMutation();
  const [switchOfferSaleVehicle] = useSwitchOfferSaleVehicleMutation();

  const attachSaleVehicleToBusinessCase = async (createdVehicleId: string) => {
    const businessCaseId = getReferrerId();

    if (!isBusinessCaseSaleReferrer || isNil(businessCaseId)) {
      return;
    }

    const businessCase = await getBusinessCase({businessCaseId})
      .unwrap()
      .catch((error: ApiError) => {
        handleApiError(error);
        return undefined;
      });

    if (isNil(businessCase)) {
      return;
    }

    const offer = businessCase.offers?.[0];
    const offerId = offer?.id;
    const hasSaleVehicle = isNotNil(offer?.saleVehicles?.[0]);

    await match({offerId, hasSaleVehicle})
      // A sale vehicle is already attached: replace it (a business case sells one vehicle).
      .with({offerId: Pattern.string, hasSaleVehicle: true}, ({offerId: existingOfferId}) =>
        switchOfferSaleVehicle({
          businessCaseId,
          offerId: existingOfferId,
          vehicleId: createdVehicleId,
        })
          .unwrap()
          .catch(handleApiError)
      )
      // The offer exists but has no sale vehicle yet: add the created vehicle to it.
      .with({offerId: Pattern.string}, ({offerId: existingOfferId}) =>
        createSaleVehicle({
          businessCaseId,
          offerId: existingOfferId,
          offerSaleVehicleRequestBody: {vehicleId: createdVehicleId},
        })
          .unwrap()
          .catch(handleApiError)
      )
      // No offer yet: create one holding the created vehicle as its sale vehicle.
      .otherwise(() =>
        createOffer({
          businessCaseId,
          offerRequestBody: {vehicleToSaleId: createdVehicleId},
        })
          .unwrap()
          .catch(handleApiError)
      );

    // The created vehicle was attached after navigating away from the business case detail,
    // so invalidate its cache to make the attached sale vehicle visible on return. The
    // switchOfferSaleVehicle and createOffer paths already invalidate, but createSaleVehicle
    // does not.
    dispatch(
      businessCaseApi.util.invalidateTags([{type: 'BusinessCaseDetail', id: businessCaseId}])
    );
  };

  return {attachSaleVehicleToBusinessCase};
};
